const SELECTOR = '.table-scroll';
const MANAGED = 'data-scroll-region';

/**
 * A `.table-scroll` wrapper that overflows sideways must be reachable by keyboard (WCAG 2.1.1),
 * otherwise tables without links or buttons cannot be scrolled without a mouse. While a wrapper
 * overflows it becomes a focusable, labelled region named after its table caption; when it fits
 * again the attributes are removed so it adds no extra tab stop.
 */
export function installScrollableRegions(root: HTMLElement = document.body): () => void {
  if (typeof ResizeObserver === 'undefined' || typeof MutationObserver === 'undefined') {
    return () => undefined;
  }
  const tracked = new WeakSet<Element>();
  const resize = new ResizeObserver((entries) => {
    for (const entry of entries) {
      const wrapper = entry.target.closest<HTMLElement>(SELECTOR);
      if (wrapper !== null) updateRegion(wrapper);
    }
  });

  function track(wrapper: HTMLElement) {
    if (tracked.has(wrapper)) return;
    tracked.add(wrapper);
    resize.observe(wrapper);
    const table = wrapper.querySelector('table');
    if (table !== null) resize.observe(table);
    updateRegion(wrapper);
  }

  function scan(node: Node) {
    if (!(node instanceof HTMLElement)) return;
    if (node.matches(SELECTOR)) track(node);
    node.querySelectorAll<HTMLElement>(SELECTOR).forEach(track);
  }

  const mutations = new MutationObserver((records) => {
    for (const record of records) record.addedNodes.forEach(scan);
  });
  mutations.observe(root, { childList: true, subtree: true });
  scan(root);

  return () => {
    mutations.disconnect();
    resize.disconnect();
  };
}

export function updateRegion(wrapper: HTMLElement): void {
  const overflowing = wrapper.scrollWidth > wrapper.clientWidth + 1;
  if (overflowing) {
    const caption = (wrapper.querySelector('caption')?.textContent ?? '').trim();
    wrapper.setAttribute(MANAGED, '');
    wrapper.setAttribute('tabindex', '0');
    wrapper.setAttribute('role', 'region');
    wrapper.setAttribute('aria-label', `${caption === '' ? 'Table' : caption} (scrolls sideways)`);
  } else if (wrapper.hasAttribute(MANAGED)) {
    wrapper.removeAttribute(MANAGED);
    wrapper.removeAttribute('tabindex');
    wrapper.removeAttribute('role');
    wrapper.removeAttribute('aria-label');
  }
}
